// ============================================================================
// CLEO'S SECRET — Plan Preview (Phase 3, UI slice, centrepiece)
// File: src/pages/PlanPreview.jsx
//
// Patient-facing centrepiece of the enrolment flow. Renders the GP-approved
// recommendation as a complete plan with:
//   • Package summary (Cinzel eyebrow + Cormorant display name + premise
//     line + accent rule + total + membership tier line)
//   • Stacked equal-weight CTAs ("Accept as recommended" / "Customise my
//     plan") — calm-CTA visual language, no primary/secondary hierarchy
//   • Service list grouped by phase (Foundations / Recalibration /
//     Optimisation), each row showing week + service name + category +
//     duration + lock glyph for mandatory milestones
//   • Sticky bottom bar that fades in once the user scrolls past the
//     primary CTA pair, sand-coloured with a subtle SAND3 top border (per
//     the calm-not-pushy memory)
//
// Customisation state is held client-side AND cached in localStorage keyed
// by recommendationId. On load we compare the cached recommendation's
// gpApprovedAt against the server's response: a mismatch (recommendation
// re-approved by the GP) clears the cache and surfaces a one-line calm
// message rather than failing loudly.
//
// "Customise my plan" mode is wired visually in this slice but the actual
// customisation controls (swap modal, catalogue browser, package switcher)
// land in the next slice. This file is the visual sanity-check at the
// biggest design surface.
//
// DEV-only: accepts a URL ?email=test_planpreview@cleotest.example so we
// can validate the UI without signing in as the test fixture user. The
// admin seed endpoint at /api/admin-seed-test-enrolment surfaces this URL.
// ============================================================================

import React, { useState, useEffect, useRef } from 'react';
import {
  FF, P, T,
  Eyebrow,
} from '../lib/design-tokens';
import Ankh from '../components/Ankh';

const LS_PREFIX = 'cleo:plan-customisations:';
const PHASE_LABEL = {
  1: 'Foundations',  2: 'Foundations',  3: 'Foundations',  4: 'Foundations',
  5: 'Recalibration', 6: 'Recalibration', 7: 'Recalibration', 8: 'Recalibration',
  9: 'Optimisation', 10: 'Optimisation', 11: 'Optimisation', 12: 'Optimisation',
};
const PHASE_RANGE = {
  Foundations:  'Weeks 1-4',
  Recalibration: 'Weeks 5-8',
  Optimisation: 'Weeks 9-12',
};
const TIER_NAME = { silver: 'Silver', gold: 'Gold', black: 'Black' };

// ─── Formatters ───
function formatAUD(cents) {
  if (cents == null) return '';
  const dollars = cents / 100;
  return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 }).format(dollars);
}
function formatDollars(cents) {
  // Plain "$X,XXX" without locale currency-symbol prefix — used by the
  // hero total so we can style the $ glyph separately from the digits.
  if (cents == null) return '';
  const dollars = Math.floor(cents / 100);
  return new Intl.NumberFormat('en-AU').format(dollars);
}
function formatApprovalDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' });
}
// "The Hormonal Pattern" → "HORMONAL". Strips the "The" prefix and
// trailing "Pattern" since the eyebrow already says "YOUR PATTERN".
function shortPatternLabel(name) {
  if (!name) return '';
  return name.replace(/^the\s+/i, '').replace(/\s+pattern$/i, '').trim().toUpperCase();
}

// ─── localStorage helpers (graceful, never throws) ───
function readCachedCustomisations(recommendationId) {
  try {
    const raw = window.localStorage.getItem(LS_PREFIX + recommendationId);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (e) { return null; }
}
function writeCachedCustomisations(recommendationId, payload) {
  try {
    window.localStorage.setItem(LS_PREFIX + recommendationId, JSON.stringify(payload));
  } catch (e) { /* silent — quota or disabled storage */ }
}
function clearCachedCustomisations(recommendationId) {
  try { window.localStorage.removeItem(LS_PREFIX + recommendationId); } catch (e) {}
}

// ─── Diamond glyph (9px rotated 45°) ───
function Diamond({ size = 9, color, opacity = 1 }) {
  return (
    <span style={{
      display: 'inline-block',
      width: size,
      height: size,
      background: color || P.BR,
      transform: 'rotate(45deg)',
      opacity,
      flexShrink: 0,
    }} aria-hidden="true" />
  );
}

// ─── Lock glyph (for mandatory milestone services) ───
function LockGlyph({ size = 14, color }) {
  const c = color || P.BR2;
  return (
    <svg width={size} height={size} viewBox="0 0 14 14" fill="none" aria-hidden="true" style={{ flexShrink: 0 }}>
      <rect x="3" y="6.5" width="8" height="6" rx="0.5" stroke={c} strokeWidth="1.1" fill="none" />
      <path d="M 4.5 6.5 V 4.5 C 4.5 2.9 5.6 1.8 7 1.8 C 8.4 1.8 9.5 2.9 9.5 4.5 V 6.5" stroke={c} strokeWidth="1.1" fill="none" />
    </svg>
  );
}

// ─── Stale-cache banner (one line, calm, dismissable) ───
function StaleBanner({ message, onDismiss }) {
  return (
    <div style={{
      maxWidth: 720,
      margin: '0 auto 24px',
      padding: '14px 20px',
      background: 'rgba(201, 169, 97, 0.10)',
      borderLeft: '2px solid ' + P.GO,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 16,
    }}>
      <span style={{ ...T.bodyMdItalic, color: P.TL }}>{message}</span>
      <button onClick={onDismiss} aria-label="Dismiss" style={{
        background: 'none', border: 'none', cursor: 'pointer',
        ...T.bodySm, color: P.TL, padding: '4px 8px',
      }}>Dismiss</button>
    </div>
  );
}

// ─── Equal-weight CTA (used for the primary pair AND the sticky bottom bar) ───
function CalmCTA({ label, onClick, full = true, compact = false, disabled = false }) {
  const [hover, setHover] = useState(false);
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      onMouseEnter={function() { setHover(true); }}
      onMouseLeave={function() { setHover(false); }}
      style={{
        width: full ? '100%' : 'auto',
        padding: compact ? '12px 18px' : '18px 24px',
        background: hover && !disabled ? 'rgba(201, 169, 97, 0.06)' : 'transparent',
        border: '1.5px solid ' + (disabled ? P.SAND3 : P.GO),
        color: disabled ? P.TF : P.BR2,
        fontFamily: FF,
        fontSize: compact ? 'clamp(15px, 1.2vw, 17px)' : 'clamp(17px, 1.4vw, 20px)',
        fontStyle: 'italic',
        fontWeight: 500,
        letterSpacing: '-0.005em',
        cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'background 0.18s ease',
        textAlign: 'center',
      }}
    >{label}</button>
  );
}

// ─── Membership tier line (small factual line under package summary) ───
function MembershipLine({ tierSlug }) {
  const tier = TIER_NAME[tierSlug] || tierSlug;
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 10,
      marginTop: 20,
      ...T.bodyMd,
      color: P.TL,
    }}>
      <Diamond size={7} color={P.GO} opacity={0.7} />
      <span><strong style={{ color: P.TL, fontWeight: 600 }}>{tier} membership</strong> included (activates Week 1)</span>
    </div>
  );
}

// ─── Service row (one per service, grouped by phase) ───
function ServiceRow({ svc }) {
  const isFixed = svc.flag === 'fixed';
  const isStronglyRec = svc.stronglyRecommended;
  return (
    <div style={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: 'clamp(12px, 2vw, 18px)',
      padding: '18px 0',
      borderTop: '1px solid rgba(200,184,136,0.35)',
    }}>
      {/* Left: week badge */}
      <div style={{
        flexShrink: 0,
        width: 'clamp(48px, 9vw, 70px)',
        textAlign: 'center',
        paddingTop: 2,
      }}>
        <div style={{ ...T.eyebrowSm, color: P.BR2 }}>WEEK</div>
        <div style={{ fontFamily: FF, fontSize: 'clamp(22px, 3.5vw, 28px)', fontWeight: 400, color: P.BR2, lineHeight: 1, marginTop: 4, letterSpacing: '-0.015em' }}>{svc.targetWeek}</div>
      </div>

      {/* Middle: service name + category + rationale snippet */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          flexWrap: 'wrap',
        }}>
          <span style={{ ...T.bodyLg, color: P.INK, fontWeight: 500 }}>{svc.service?.name || '(service)'}</span>
          {isFixed && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, ...T.bodySmItalic, color: P.BR2 }}>
              <LockGlyph /> Required for graduation
            </span>
          )}
          {isStronglyRec && !isFixed && (
            <span style={{ ...T.bodySmItalic, color: P.BR2 }}>Strongly recommended</span>
          )}
        </div>
        <div style={{ ...T.bodySm, color: P.TL, fontWeight: 500, marginTop: 4 }}>
          {/* Capitalise only the category (allied_health → Allied health), not the duration. */}
          <span style={{ textTransform: 'capitalize' }}>{(svc.service?.category || '').replace(/_/g, ' ')}</span>
          {svc.service?.durationMinutes ? ' · ' + svc.service.durationMinutes + ' min' : ''}
        </div>
        {svc.rationale && (
          <div style={{ ...T.bodyMdItalic, color: P.TL, marginTop: 8, maxWidth: 540 }}>
            {svc.rationale}
          </div>
        )}
      </div>

      {/* Right: price if not package-included */}
      <div style={{ flexShrink: 0, textAlign: 'right', minWidth: 64 }}>
        {svc.isPackageIncluded ? (
          <span style={{ ...T.bodySmItalic, color: P.BR2 }}>Included</span>
        ) : (
          <span style={{ ...T.bodyMd, color: P.INK, fontWeight: 600 }}>{formatAUD(svc.priceCents)}</span>
        )}
      </div>
    </div>
  );
}

// ─── Phase group (header + service rows) ───
function PhaseGroup({ phase, services }) {
  if (services.length === 0) return null;
  return (
    <section style={{ margin: '48px auto 0', maxWidth: 760 }}>
      <div style={{
        display: 'flex',
        alignItems: 'baseline',
        justifyContent: 'space-between',
        gap: 16,
        marginBottom: 8,
        paddingBottom: 12,
        borderBottom: '1px solid ' + P.SAND3,
      }}>
        <div style={{ ...T.label, color: P.BR2 }}>{phase}</div>
        <div style={{ ...T.bodySmItalic, color: P.BR2 }}>{PHASE_RANGE[phase]}</div>
      </div>
      <div>
        {services.map(function(svc, i) {
          return <ServiceRow key={svc.serviceId + '-' + svc.targetWeek + '-' + i} svc={svc} />;
        })}
      </div>
    </section>
  );
}

// ─── Sticky bottom bar (fades in on scroll past the primary CTAs) ───
function StickyCTABar({ visible, onAccept, onCustomise }) {
  return (
    <div style={{
      position: 'fixed',
      left: 0,
      right: 0,
      bottom: 0,
      background: P.SAND,
      borderTop: '1px solid ' + P.SAND3,
      padding: '14px 20px calc(14px + env(safe-area-inset-bottom, 0px))',
      boxShadow: '0 -4px 16px rgba(53,39,32,0.06)',
      transition: 'transform 0.28s ease, opacity 0.28s ease',
      transform: visible ? 'translateY(0)' : 'translateY(100%)',
      opacity: visible ? 1 : 0,
      pointerEvents: visible ? 'auto' : 'none',
      zIndex: 50,
    }}>
      <div style={{
        maxWidth: 720,
        margin: '0 auto',
        display: 'flex',
        gap: 12,
      }}>
        <div style={{ flex: 1 }}><CalmCTA label="Accept as recommended" onClick={onAccept} compact /></div>
        <div style={{ flex: 1 }}><CalmCTA label="Customise my plan" onClick={onCustomise} compact /></div>
      </div>
    </div>
  );
}

// ─── Customise placeholder (stub for the next slice) ───
function CustomisePlaceholder({ onBack }) {
  return (
    <section style={{
      maxWidth: 720,
      margin: '32px auto 0',
      padding: '32px 28px',
      background: P.PAP,
      border: '1px solid ' + P.SAND3,
    }}>
      <div style={{ ...T.label, color: P.BR2, marginBottom: 16 }}>CUSTOMISATION</div>
      <p style={{ ...T.bodyLg, color: P.TX, margin: '0 0 16px' }}>
        The swap, add, and package-switch controls land in the next session.
        For now, you can return to the accept-as-recommended view or continue
        to checkout with the recommendation as-is.
      </p>
      <button onClick={onBack} style={{
        background: 'none',
        border: 'none',
        color: P.BR2,
        fontFamily: FF,
        fontStyle: 'italic',
        fontSize: 'clamp(15px, 1.2vw, 17px)',
        cursor: 'pointer',
        padding: 0,
      }}>← Back to the recommended view</button>
    </section>
  );
}

// ═════════════════════════════════════════════════════════════════════════
//                              MAIN PAGE
// ═════════════════════════════════════════════════════════════════════════
export default function PlanPreview({ onNavigate, email, devEmail, redirectMessage }) {
  const effectiveEmail = email || devEmail;

  const [loading, setLoading] = useState(true);
  const [recomputing, setRecomputing] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [mode, setMode] = useState('accept'); // 'accept' | 'customise'
  // Redirect-back messages (from PlanCheckout when no plan was passed) seed
  // the same staleMsg banner used for cache-staleness; one mechanism, two
  // sources. Caller's redirectMessage takes priority on first mount.
  const [staleMsg, setStaleMsg] = useState(redirectMessage || null);

  // Customisation state (for next slice; thread through to checkout)
  const [customisations, setCustomisations] = useState({
    paymentOption: null,
    packageId: null,
    serviceEdits: [],
    addedServices: [],
  });

  // Sticky-bar visibility tracked via IntersectionObserver on the primary CTA.
  const primaryCtaRef = useRef(null);
  const [stickyVisible, setStickyVisible] = useState(false);

  // ── Initial fetch + cache hydration ────────────────────────────────────
  useEffect(function() {
    if (!effectiveEmail) {
      setError('No email provided. Sign in or follow the link from your email.');
      setLoading(false);
      return;
    }

    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        // 1. Fetch the pristine plan first
        const pristineRes = await fetch('/api/program-plan-preview', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: effectiveEmail }),
        });
        const pristine = await pristineRes.json();
        if (!pristineRes.ok) {
          if (cancelled) return;
          setError(pristine.error || pristine.message || 'Could not load your plan.');
          setLoading(false);
          return;
        }
        if (cancelled) return;

        // 2. Check localStorage for cached customisations
        const recommendationId = pristine.recommendation?.id;
        const cached = recommendationId ? readCachedCustomisations(recommendationId) : null;

        let finalData = pristine;
        let finalCustomisations = {
          paymentOption: pristine.payments?.selectedOption || null,
          packageId: pristine.package?.id || null,
          serviceEdits: [],
          addedServices: [],
        };

        if (cached) {
          // Stale-check: cached gpApprovedAt must match server's
          if (cached.gpApprovedAt !== pristine.recommendation?.gpApprovedAt) {
            clearCachedCustomisations(recommendationId);
            setStaleMsg('Your plan has been updated since you last visited. Showing the latest version.');
          } else {
            // Apply cached customisations: re-fetch with them
            try {
              const reRes = await fetch('/api/program-plan-preview', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  email: effectiveEmail,
                  paymentOption: cached.paymentOption,
                  packageId: cached.packageId,
                  serviceEdits: cached.serviceEdits,
                  addedServices: cached.addedServices,
                }),
              });
              const reData = await reRes.json();
              if (reRes.ok) {
                finalData = reData;
                finalCustomisations = {
                  paymentOption: cached.paymentOption,
                  packageId: cached.packageId,
                  serviceEdits: cached.serviceEdits || [],
                  addedServices: cached.addedServices || [],
                };
                if (cached.mode === 'customise') setMode('customise');
              } else {
                // Cached customisations no longer valid (service removed, etc).
                // Drop them and surface a calm message.
                clearCachedCustomisations(recommendationId);
                setStaleMsg('Your earlier customisations are no longer applicable. Showing the latest recommended version.');
              }
            } catch (e) {
              // Network glitch during re-apply — fall back to pristine.
              clearCachedCustomisations(recommendationId);
            }
          }
        }

        if (!cancelled) {
          setData(finalData);
          setCustomisations(finalCustomisations);
          setLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          setError('Could not connect to the server. Please try again.');
          setLoading(false);
        }
      }
    }

    load();
    return function() { cancelled = true; };
  }, [effectiveEmail]);

  // ── Sticky-bar observer ────────────────────────────────────────────────
  useEffect(function() {
    if (!primaryCtaRef.current) return;
    const el = primaryCtaRef.current;
    const observer = new IntersectionObserver(function(entries) {
      const e = entries[0];
      setStickyVisible(!e.isIntersecting && e.boundingClientRect.top < 0);
    }, { threshold: 0 });
    observer.observe(el);
    return function() { observer.disconnect(); };
  }, [loading, mode]);

  // ── Handlers ───────────────────────────────────────────────────────────
  function handleAccept() {
    if (!data?.recommendation?.id || !onNavigate) return;
    // Persist current selection to localStorage (refresh-recovery for the
    // checkout page, plus a record of what the patient committed to).
    writeCachedCustomisations(data.recommendation.id, {
      gpApprovedAt: data.recommendation.gpApprovedAt,
      paymentOption: customisations.paymentOption,
      packageId: customisations.packageId,
      serviceEdits: [],
      addedServices: [],
      mode: 'accept',
    });
    // Navigate to PlanCheckout with the full customisation payload as
    // routePayload (props-first; PlanCheckout falls back to localStorage
    // on direct-URL navigation only). devEmail is threaded through because
    // App.jsx's navigate() replaces routePayload entirely on every nav, so
    // the DEV email-override would otherwise be lost on this hop. (When
    // there's a real Supabase session, App.jsx's patientEmail covers it
    // and devEmail is undefined harmlessly.)
    onNavigate('plan-checkout', {
      recommendationId: data.recommendation.id,
      packageId: customisations.packageId,
      paymentOption: customisations.paymentOption,
      serviceEdits: [],
      addedServices: [],
      devEmail: devEmail,
    });
  }

  function handleCustomise() {
    setMode('customise');
    if (data?.recommendation?.id) {
      writeCachedCustomisations(data.recommendation.id, {
        gpApprovedAt: data.recommendation.gpApprovedAt,
        paymentOption: customisations.paymentOption,
        packageId: customisations.packageId,
        serviceEdits: customisations.serviceEdits,
        addedServices: customisations.addedServices,
        mode: 'customise',
      });
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function handleBackToAccept() {
    setMode('accept');
    if (data?.recommendation?.id) {
      writeCachedCustomisations(data.recommendation.id, {
        gpApprovedAt: data.recommendation.gpApprovedAt,
        paymentOption: customisations.paymentOption,
        packageId: customisations.packageId,
        serviceEdits: customisations.serviceEdits,
        addedServices: customisations.addedServices,
        mode: 'accept',
      });
    }
  }

  // ── Render ─────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: P.SAND,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        gap: 20,
      }}>
        <Ankh size={48} color={P.GO} opacity={0.55} />
        <div style={{ ...T.bodyMdItalic, color: P.BR2 }}>Preparing your plan...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ minHeight: '100vh', background: P.SAND, padding: 'clamp(48px, 8vw, 96px) 24px' }}>
        <div style={{ maxWidth: 640, margin: '0 auto', textAlign: 'center' }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 32 }}>
            <Ankh responsive="opener" color={P.GO} />
          </div>
          <h1 style={{ ...T.displaySm, color: P.INK, margin: '0 0 20px' }}>
            <span style={{ display: 'block' }}>Your plan</span>
            <span style={{ display: 'block', fontStyle: 'italic', color: P.BR2, fontSize: '0.9em' }}>is not yet ready.</span>
          </h1>
          <p style={{ ...T.bodyMd, color: P.TL, maxWidth: 480, margin: '0 auto' }}>{error}</p>
        </div>
      </div>
    );
  }

  // Group services by phase
  const grouped = { Foundations: [], Recalibration: [], Optimisation: [] };
  (data.services || []).forEach(function(s) {
    const phase = PHASE_LABEL[s.targetWeek] || 'Foundations';
    grouped[phase].push(s);
  });

  const selectedSchedule = data.payments?.schedule || [];
  const isInstalment = data.payments?.selectedOption === 'three_instalments';

  return (
    <div style={{
      // width:100vw + overflowX:hidden isolates this page from any document
      // overflow the rest of the shell creates (Navigation's flex children
      // overflow at narrow widths and would otherwise push our content off-
      // viewport). Without this, max-width:880 + margin:auto centres within
      // the wider-than-viewport document rather than within the visible area.
      width: '100vw',
      maxWidth: '100vw',
      overflowX: 'hidden',
      minHeight: '100vh',
      background: P.SAND,
      paddingBottom: 120, // leave room for sticky bar
    }}>
      <div style={{
        // <div> not <main> — App.jsx already wraps pages in a <main>; nested
        // <main> elements are invalid HTML and can confuse user-agent styles.
        maxWidth: 880,
        margin: '0 auto',
        padding: 'clamp(40px, 6vw, 80px) clamp(20px, 4vw, 40px)',
      }}>
        {staleMsg && <StaleBanner message={staleMsg} onDismiss={function() { setStaleMsg(null); }} />}

        {/* ── TOP: package summary ─────────────────────────────────────── */}
        <div style={{ textAlign: 'center' }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 32 }}>
            <Ankh responsive="opener" color={P.GO} />
          </div>
          <div>
            <Eyebrow center>Your 12-week program</Eyebrow>
          </div>

          <h1 style={{
            ...T.display,
            fontSize: 'clamp(34px, 5vw, 64px)',
            color: P.INK,
            margin: '24px auto 18px',
            maxWidth: 720,
          }}>
            <span style={{ display: 'block' }}>{data.package?.displayName || 'Your program'}</span>
          </h1>

          {data.recommendation?.gpApprovedAt && (
            <p style={{
              ...T.premise,
              fontSize: 'clamp(17px, 1.6vw, 22px)',
              color: P.BR2,
              margin: '0 auto',
              maxWidth: 600,
            }}>
              Approved by {data.recommendation.approvedByName || 'Dr Robyn O\'Sullivan'} on {formatApprovalDate(data.recommendation.gpApprovedAt)}.
            </p>
          )}

          {/* Accent rule */}
          <div style={{
            width: 'clamp(180px, 22vw, 280px)',
            height: 2,
            background: P.GO,
            opacity: 0.85,
            margin: '32px auto 28px',
          }} />

          {/* Total — $ glyph at 0.7em so the digits visually dominate */}
          <div style={{
            fontFamily: FF,
            fontSize: 'clamp(32px, 4vw, 52px)',
            fontWeight: 400,
            letterSpacing: '-0.015em',
            lineHeight: 1,
            color: P.INK,
          }}>
            <span style={{ fontSize: '0.7em', verticalAlign: 'baseline', marginRight: 2 }}>$</span>{formatDollars(data.totals?.totalCents)}
          </div>
          {data.payments?.availableOptions?.length > 1 && (
            <div style={{ ...T.bodySmItalic, color: P.TL, marginTop: 8 }}>
              {isInstalment
                ? 'Three instalments. ' + formatAUD(selectedSchedule[0]?.amountCents) + ' today, balance over 8 weeks.'
                : 'Upfront, or pay in three instalments.'}
            </div>
          )}

          {/* Membership tier line */}
          {data.package?.bundledMembershipTier && (
            <MembershipLine tierSlug={data.package.bundledMembershipTier} />
          )}
        </div>

        {/* ── Stacked equal-weight CTAs ───────────────────────────────── */}
        <div ref={primaryCtaRef} style={{
          maxWidth: 480,
          margin: '40px auto 8px',
          display: 'flex',
          flexDirection: 'column',
          gap: 14,
        }}>
          <CalmCTA label="Accept as recommended" onClick={handleAccept} />
          <CalmCTA label="Customise my plan" onClick={handleCustomise} />
        </div>

        {/* ── Customise mode placeholder ──────────────────────────────── */}
        {mode === 'customise' && (
          <CustomisePlaceholder onBack={handleBackToAccept} />
        )}

        {/* ── Service list by phase ───────────────────────────────────── */}
        <PhaseGroup phase="Foundations"   services={grouped.Foundations} />
        <PhaseGroup phase="Recalibration" services={grouped.Recalibration} />
        <PhaseGroup phase="Optimisation"  services={grouped.Optimisation} />

        {/* ── GP pattern context (calm closing note) ──────────────────── */}
        {data.pattern?.narrativeText && (
          <section style={{
            maxWidth: 720,
            margin: '64px auto 0',
            padding: '32px 28px',
            background: P.PAP,
            border: '1px solid ' + P.SAND3,
          }}>
            <div style={{ ...T.label, color: P.BR2, marginBottom: 14 }}>
              YOUR PATTERN{data.pattern.name ? ' · ' + shortPatternLabel(data.pattern.name) : ''}
            </div>
            <p style={{ ...T.bodyMd, color: P.TX, margin: 0, lineHeight: 1.8 }}>
              {data.pattern.narrativeText}
            </p>
          </section>
        )}

        {/* Inline recomputing indicator (calm, non-blocking) */}
        {recomputing && (
          <div style={{
            ...T.bodyMdItalic,
            color: P.BR2,
            textAlign: 'center',
            marginTop: 24,
          }}>Updating your plan...</div>
        )}
      </div>

      <StickyCTABar visible={stickyVisible} onAccept={handleAccept} onCustomise={handleCustomise} />
    </div>
  );
}
